"use client";

import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const leftX = useTransform(scrollYProgress, [0, 1], [0, -400]);
  const rightX = useTransform(scrollYProgress, [0, 1], [0, 400]);

  return (
    <section ref={sectionRef} className="relative min-h-0 lg:min-h-screen flex flex-col items-center justify-center overflow-hidden bg-surface py-16 sm:py-20 md:py-[60px] lg:py-[80px]">

      {/* Red glow at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[300px] z-[1] pointer-events-none dark:opacity-100 opacity-40" style={{
        background: "radial-gradient(ellipse 80% 100% at 50% 100%, rgba(225,29,36,0.12) 0%, rgba(225,29,36,0.04) 50%, transparent 80%)"
      }} />

      {/* Left Mockups */}
      <motion.div
        className="absolute left-0 xl:left-[10px] top-[55%] -translate-y-1/2 w-[330px] xl:w-[390px] h-[530px] xl:h-[585px] hidden lg:block z-[2]"
        initial={{ opacity: 0, x: -60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
        style={{ x: leftX }}
      >
        <div className="w-full h-full animate-[float_6s_ease-in-out_infinite]">
          <Image src="/assets/hero/Mockups.avif" alt="Project mockups" fill className="object-contain object-left" priority />
        </div>
      </motion.div>

      {/* Right Mockups */}
      <motion.div
        className="absolute right-0 xl:right-[10px] top-[55%] -translate-y-1/2 w-[435px] xl:w-[495px] h-[540px] xl:h-[600px] hidden lg:block z-[2]"
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
        style={{ x: rightX }}
      >
        <div className="w-full h-full animate-[float_7s_ease-in-out_infinite_0.5s]">
          <Image src="/assets/hero/Frame.avif" alt="App screens" fill className="object-contain object-right" priority />
        </div>
      </motion.div>

      {/* Top Logo */}
      <motion.div
        className="absolute top-7 left-1/2 -translate-x-1/2 z-10"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Image src="/assets/devcubix-logo-dark.png" alt="Dev Cubix" width={280} height={114} className="h-10 sm:h-12 md:h-16 w-auto object-contain dark:hidden block" priority />
        <Image src="/assets/devcubix-logo-white.png" alt="Dev Cubix" width={280} height={114} className="h-10 sm:h-12 md:h-16 w-auto object-contain hidden dark:block" priority />
      </motion.div>

      {/* ===== Center Content ===== */}
      <div className="relative z-10 text-center max-w-5xl mx-auto px-6">

        {/* FIX #5: Rating badge — 32px circles, overlapping, compact like DM's 57×32px */}
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center mb-1.5">
            {/* Google G — 32px circle */}
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md z-[2]">
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </div>
            {/* Trustpilot */}
            <div className="w-8 h-8 rounded-full bg-[#00B67A] flex items-center justify-center shadow-md -ml-2 z-3 border-2 border-black">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                <path d="M12 1.5l2.76 8.5h8.94l-7.23 5.26 2.76 8.5L12 18.5l-7.23 5.26 2.76-8.5L.3 10h8.94L12 1.5z" fill="white"/>
              </svg>
            </div>
            {/* Fiverr */}
            <div className="w-8 h-8 rounded-full bg-[#1DBF73] flex items-center justify-center shadow-md -ml-2 z-4 border-2 border-black">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white">
                <path d="M16.25 16.25v-10h-2.5v10h2.5zm-5 0v-7.5h2.5v-2.5h-2.5V5c0-.69.56-1.25 1.25-1.25h1.25v-2.5h-1.25A3.75 3.75 0 008.75 5v1.25h-1.5v2.5h1.5v7.5h2.5zm7.5-12.5a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5z"/>
              </svg>
            </div>
            {/* Upwork */}
            <div className="w-8 h-8 rounded-full bg-[#14A800] flex items-center justify-center shadow-md -ml-2 z-5 border-2 border-black">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="white">
                <path d="M18.56 12.93c-1.09 0-2.09-.53-2.87-1.38l.43-2.02.01-.07c.34-1.87 1.31-3.17 2.88-3.17 1.52 0 2.76 1.24 2.76 2.76s-1.24 2.88-3.21 2.88zm0-9.05c-2.92 0-5.1 1.91-5.96 4.81-.01.02-1.37-2.17-1.86-3.25H7.75v6.69c0 1.36-1.1 2.46-2.46 2.46s-2.46-1.1-2.46-2.46V5.44H.08v6.69c0 2.87 2.34 5.21 5.21 5.21s5.21-2.34 5.21-5.21V10.6c.48.86 1.07 1.78 1.77 2.58l-1.5 7.06h2.83l1.09-5.1c1.01.68 2.19 1.09 3.52 1.09 3.22 0 5.71-2.66 5.71-5.88s-2.49-5.47-5.36-5.47z"/>
              </svg>
            </div>
          </div>

          {/* Stars — 14px / 400 weight */}
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm font-normal" style={{ color: "var(--foreground)" }}>4.9</span>
            <Image src="/assets/hero/Star-rating.svg" alt="5 stars" width={88} height={16} className="h-4 w-auto" />
          </div>

          {/* FIX #6: Tagline — font-normal (400), no tracking, mt-2.5 */}
          <p className="text-sm font-normal mt-2.5" style={{ color: "var(--foreground)" }}>Design that converts. Simple as that.</p>
        </motion.div>

        {/* FIX #1,2,3: HEADING — Red Hat Display, bold italic, leading-[1.2], normal tracking */}
        {/* FIX #8: mt-6 (24px gap from rating block) */}
        <motion.h1
          className="text-[28px] sm:text-4xl md:text-5xl lg:text-6xl xl:text-[72px] font-bold leading-[1.15] mb-0 mt-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <span className="block">
            <span className="font-ui text-foreground">We </span>
            <span className="font-serif italic font-bold text-foreground">Design </span>
            <span className="font-ui text-foreground">Products</span>
          </span>
          <span className="block">
            <span className="font-ui text-foreground">That Drive </span>
            <span className="inline-block align-middle relative -top-[0.05em] mx-0.5 sm:mx-1">
              <Image
                src="/assets/hero/Icons.svg"
                alt="Figma, Webflow, Framer"
                width={144}
                height={56}
                className="h-5 w-[56px] sm:h-8 sm:w-24 md:h-[48px] md:w-[128px] lg:h-[56px] lg:w-[144px] inline-block"
              />
            </span>
            <span className="font-ui text-foreground"> Results</span>
          </span>
        </motion.h1>

        {/* FIX #3: Country flags pill — with green border, 24px gap from headline */}
        <motion.div
          className="mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="inline-flex items-center gap-2.5 sm:gap-3 flex-wrap justify-center border border-red-accent/60 rounded-[24px] px-4 sm:px-5 py-2 sm:py-2.5 text-[11px] sm:text-sm text-foreground/60">
            <span className="inline-flex items-center gap-1.5">
              <Image src="https://flagcdn.com/w20/us.png" alt="US" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
              <Image src="https://flagcdn.com/w20/gb.png" alt="GB" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
              <Image src="https://flagcdn.com/w20/au.png" alt="AU" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
              <Image src="https://flagcdn.com/w20/it.png" alt="IT" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
            </span>
            <span className="font-body">Designing across 8+ countries</span>
            <span className="inline-flex items-center gap-1.5">
              <Image src="https://flagcdn.com/w20/pk.png" alt="PK" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
              <Image src="https://flagcdn.com/w20/ae.png" alt="AE" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
              <Image src="https://flagcdn.com/w20/sg.png" alt="SG" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
              <Image src="https://flagcdn.com/w20/za.png" alt="ZA" width={20} height={15} unoptimized className="w-4 h-auto rounded-xs" />
            </span>
          </div>
        </motion.div>

        {/* FIX #7: 56px gap (mt-14) from pill to CTA */}
        {/* FIX #9: Button — gradient bg, rounded-lg (8px), inset white glow, font-bold */}
        <motion.div
          className="mt-8 sm:mt-10 md:mt-14"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8, ease: [0.25, 1, 0.5, 1] }}
        >
          {/* Rotating gradient glow wrapper */}
          <span className="relative inline-block rounded-lg p-[2px] gradient-btn-bg">
            <Link
              href="/contact"
              className="relative z-10 inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 rounded-[6px] text-white font-normal text-base hover:opacity-90 transition-all duration-300"
              style={{
                background: "linear-gradient(rgb(180, 20, 28) 25%, rgb(225, 29, 36))",
                boxShadow: "rgba(255, 255, 255, 0.72) 0px 1.6px 3px 0px inset",
              }}
            >
              <svg className="w-4.5 h-4.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z"/>
              </svg>
              Book a Call
            </Link>
          </span>
        </motion.div>

      </div>

      {/* Tablet mockup — only show on medium screens (sm-lg), hidden on phone and desktop */}
      <div className="relative z-10 mt-8 hidden sm:flex lg:hidden justify-center w-full px-6">
        <div className="relative w-full max-w-[320px] h-[220px]">
          <Image src="/assets/hero/Mockups.avif" alt="Project mockups" fill className="object-contain" />
        </div>
      </div>

    </section>
  );
}
